"use client";

import { useState, useEffect, useRef } from "react";

interface RelatedEvent {
  slug: string;
  title: string;
  image?: string | null;
  icon?: string | null;
  markets?: { outcomePrices?: number[] }[];
}

// Module-level cache shared across all instances
const cache = new Map<string, { events: RelatedEvent[]; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const inflight = new Map<string, Promise<RelatedEvent[]>>();

/**
 * Shared hook to fetch related events for an event sidebar.
 * Deduplicates calls across multiple GenericEventSidebar instances.
 */
export function useRelatedEvents(topMarketSlug: string | null, eventSlug: string) {
  const [relatedEvents, setRelatedEvents] = useState<RelatedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    if (!topMarketSlug) {
      setLoading(false);
      return;
    }

    const cacheKey = topMarketSlug;

    // Check cache
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      const filtered = cached.events.filter(e => e.slug !== eventSlug).slice(0, 4);
      setRelatedEvents(filtered);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Deduplicate in-flight requests
    let promise = inflight.get(cacheKey);
    if (!promise) {
      promise = fetch(`/api/recommendations/similar/${topMarketSlug}?limit=4`)
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          const events: RelatedEvent[] = data?.events || [];
          cache.set(cacheKey, { events, ts: Date.now() });
          inflight.delete(cacheKey);
          return events;
        })
        .catch(() => {
          inflight.delete(cacheKey);
          return [] as RelatedEvent[];
        });
      inflight.set(cacheKey, promise);
    }

    promise.then(events => {
      if (cancelledRef.current) return;
      const filtered = events.filter(e => e.slug !== eventSlug).slice(0, 4);
      setRelatedEvents(filtered);
      setLoading(false);
    });

    return () => {
      cancelledRef.current = true;
    };
  }, [topMarketSlug, eventSlug]);

  return { relatedEvents, loading };
}
